import { Component, Input, Output, EventEmitter, ViewEncapsulation, ViewChild, ElementRef } from '@angular/core';
import { ProjectService } from '../../../providers';
import { Project } from '../../../models';
import { tap } from 'rxjs/operators';
import { AutoCompleteComponent } from '../auto-complete/auto-complete.component';

@Component({
    selector: 'auto-complete-projects',
    templateUrl: './auto-complete-projects.component.html',
    styleUrls: ['./auto-complete-projects.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AutoCompleteProjectComponent {

    @Output() selectionChanged = new EventEmitter<Project[]>();
    @Input() set initSelectedList(projects: Array<Project>) {
        // Init project list with projects already selected (OBS this will happen every time an change is emitted)
        // Make of slice copy of array (otherwise functions like pop() will change the original array)
        this.selectedProjects = projects.slice(0);
    }

    @Input() set initSelectedKeys(projectKeys: Array<string>) {
        // Same as initSelectedList but with keys
        // Init project list with projects already selected (OBS this will happen every time an change is emitted)
        // Make of slice copy of array (otherwise functions like pop() will change the original array)
        this.selectedProjectKeys = projectKeys.slice(0);
    }

    // Set the maximum number of projects to be selected at a time
    @Input() maxMultiSelect: number = -1;

    @ViewChild('ProjectAutoComplete', { static: true }) autoCompleteComponent: AutoCompleteComponent;

    // List of user selected / projects
    public selectedProjects: Array<Project> = [];
    public selectedProjectKeys: Array<string> = null;

    // Observable to be used in autocompletion list
    public projectsObservable;
    // Filter function to select motes
    public filterFunction;

    // Make document available in view
    public document = document;

    constructor(private projectService: ProjectService) {
        // Wrap filter function in ES6 container in order to preserve conrext
        this.filterFunction = (item, idx, searchString) => this.filterProjects(item, idx, searchString);
    }

    ngOnInit() {
        // Get observable of projects to be used in list
        this.projectsObservable = this.projectService.watchAllProjects().pipe(tap((projects) => {
            if(this.selectedProjectKeys == null) return;
            // Find selected projects (from project keys)
            this.selectedProjects = this.selectedProjectKeys.map(key => projects.find(p => (key == p.key)))
                .filter(p => p ? true : false);
            // Initially notify listners
            this.selectionChanged.emit(this.selectedProjects);
        }));
    }

    public itemSelected(item: Project) {
        // Add new item to selection
        this.selectedProjects.push(item);
        // Pop old items if max selection count is enabled
        if(this.maxMultiSelect > 0 && this.selectedProjects.length > this.maxMultiSelect) {
            this.selectedProjects.shift();
        }
        this.selectionChanged.emit(this.selectedProjects);
    }

    public itemUnselected(rmItem: Project) {
        this.selectedProjects = this.selectedProjects.filter((item: Project) => {
            // Remove mote from list (return false if this is the one to be remove)
            return !(item.key == rmItem.key);
        });
        this.selectionChanged.emit(this.selectedProjects);
    }

    public queryDeleted() {
        if(this.selectedProjects.length <= 0) return;
        // Delete last entry in list of project
        this.selectedProjects.pop();
        this.selectionChanged.emit(this.selectedProjects);
    }

    public openList() {
        this.autoCompleteComponent.toggleOpen();
    }

    public filterProjects(item: Project, idx: number, searchString: string): boolean {
        if(!searchString || idx >= 1000) return false;

        // Filter away projects that are already selected
        if(this.selectedProjects.find((searchProject) => searchProject.key == item.key)) return false;

        // Return all if * is used
        if(searchString == "*") return true;

        // Search without case sensitivity
        let query = searchString.toLocaleLowerCase();

        // Search for string matches
        return item.key.startsWith(query) || item.title.toLowerCase().includes(query);
    }
}
